<template>
  <v-container style="width: 800px;">
    <v-card>
      <v-container>
        <v-card-title>{{ isUpdateMode ? $t('update-btn') : $t('save-btn') }}</v-card-title>

        <!-- Enter 리로드 방지 -->
        <form @submit.prevent>
          <v-flex sm12>
            <v-text-field
              v-model.trim="userId"
              v-validate="'required'"
              data-vv-name="userId"
              :error-messages="
                errors.has('userId')
                  ? ['User IDを入力してください。']
                  : []
              "
              :disabled="isUpdateMode"
              :aria-required="!isUpdateMode"
              required
            >
              <template v-slot:label>
                <span>User ID</span>
                <span
                  v-if="!isUpdateMode"
                  class="required-star"
                  aria-hidden="true"
                >
                  *
                </span>
             </template>          
            </v-text-field>
          </v-flex>

          <v-flex xs12 sm12>
            <v-select
              v-model="position"
              :items="positions"
              item-text="positionName"
              item-value="positionCode"
              v-validate="'required'"
              data-vv-name="position"
              :error-messages="
                errors.has('position')
                  ? ['Positionを選択してください。']
                  : []
              "
              required
            >
              <template v-slot:label>
                <span>Position</span>
                <span class="required-star" aria-hidden="true">*</span>
              </template>
            </v-select>
          </v-flex>          

          <v-flex xs12 sm12>
            <v-autocomplete
              v-model="selectedRegionCodes"
              :items="selectableRegions"
              item-text="displayName"
              item-value="regionCode"
              :loading="regionLoading"
              :disabled="regionLoading || isAdminPosition"
              v-validate="{ required: isRegionRequired }"
              data-vv-name="regions"
              :error-messages="
                isRegionRequired && errors.has('regions')
                  ? ['地域を1つ以上選択してください。']
                  : []
              "
              multiple
              chips
              small-chips
              :deletable-chips="!isAdminPosition"
              :clearable="!isAdminPosition"
            >
              <template v-slot:label>
                <span>Region</span>
                <span
                  v-if="isRegionRequired"
                  class="required-star"
                  aria-hidden="true"
                >
                  *
                </span>
              </template>
            </v-autocomplete>
          </v-flex>

          <v-flex xs12 sm12>
            <v-autocomplete
              v-model="selectedStoreCodes"
              :items="filteredStores"
              item-text="displayName"
              item-value="storeCode"
              v-validate="{ required: isStoreRequired }"
              data-vv-name="stores"
              :error-messages="
                isStoreRequired && errors.has('stores')
                  ? ['店舗を1つ以上選択してください。']
                  : []
              "
              :loading="storeLoading"
              :disabled="
                storeLoading ||
                isAdminPosition ||
                selectedRegionCodes.length === 0
              "
              multiple
              chips
              small-chips
              deletable-chips
              clearable
            >
              <template v-slot:label>
                <span>Store</span>
                <span
                  v-if="isStoreRequired"
                  class="required-star"
                  aria-hidden="true"
                >
                  *
                </span>
              </template>

              <template v-slot:no-data>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      {{ $t('noTableDataAvailable') }}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </template>
            </v-autocomplete>
          </v-flex>

          <div class="button-area">
            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-btn
                  block
                  color="#4C728A"
                  dark
                  :to="{ name: 'admin/ocr-user-store-mapping' }"
                >
                  <v-icon dark left>arrow_back</v-icon>
                  {{ $t('back-btn') }}
                </v-btn>
              </v-flex>
              <v-flex xs12 sm6>
                <v-btn
                  block
                  color="success"
                  dark
                  @click="submit"
                  :loading="loading2"
                  :disabled="loading2"
                >
                  <v-icon class="ma-2" dark small>save</v-icon>
                  {{ isUpdateMode ? $t('update-btn') : $t('save-btn') }}
                </v-btn>
              </v-flex>
            </v-layout>
          </div>

        </form>
      </v-container>
    </v-card>    
  </v-container>
</template>

<script>
import Vue from 'vue'
import VeeValidate from 'vee-validate'
import ServerApiCallService from '@/services/ServerApiCallService.js'
import { OCR_ACCESS_ROLES } from '@/util/ocrAccessRoles'

Vue.use(VeeValidate)

export default {
  async created() {

    ServerApiCallService.setAppJs(this.getAppComponent())

    const { userId = '' } = this.$route.params || {}

    this.isUpdateMode = userId !== ''
    this.userId = userId

    // 지역과 점포 목록을 동시에 조회
    await Promise.all([
      this.loadRegions(),
      this.loadStores(),
    ])

    // 수정 화면이면 기존 선택값 조회
    if (this.isUpdateMode) {
      await this.loadUserMapping(userId)
    }
  },
  computed: {
    // 현재 언어에 맞는 포지션 목록을 생성
    positions() {
      return OCR_ACCESS_ROLES.map(role => ({
        positionCode: role.code,
        positionName: this.$t(role.nameKey),
      }))
    },

    // 관리자 여부
    isAdminPosition() {
      return this.position === 'ADMIN'
    },

    // 관리자를 제외한 모든 포지션은 지역 필수
    isRegionRequired() {
      return this.position !== 'ADMIN'
    },

    // 점포리더와 영업은 점포 필수
    isStoreRequired() {
      return ['STORE_LEADER', 'SALES'].includes(this.position)
    },

    filteredStores() {
      if (this.selectedRegionCodes.length === 0) {
        return []
      }

      const selectedCodes = this.selectedRegionCodes.map(String)

      return this.stores.filter(store =>
        selectedCodes.includes(store.prefectureCode)
      )
    },
   

    selectableRegions() {
      // 관리자는 전지역만 표시
      if (this.isAdminPosition) {
        return this.regions.filter(region =>
          String(region.regionCode) === '99'
        )
      }

      // 다른 포지션은 전지역을 제외
      return this.regions.filter(region =>
        String(region.regionCode) !== '99'
      )
    },
  },
  watch: {
    selectedRegionCodes() {
      // 관리자 + 전지역은 모든 점포가 유효
      if (
        this.isAdminPosition &&
        this.selectedRegionCodes.includes('99')
      ) {
        return
      }

      const validStoreCodes = new Set(
        this.filteredStores.map(store =>
          String(store.storeCode)
        )
      )

      this.selectedStoreCodes = this.selectedStoreCodes.filter(
        storeCode => validStoreCodes.has(String(storeCode))
      )
    },

    position(newPosition) {
      // 관리자
      if (newPosition === 'ADMIN') {
        this.selectedRegionCodes = ['99']
        this.selectedStoreCodes = []

        this.errors.remove('regions')
        this.errors.remove('stores')
        return
      }

      // 관리자가 아니면 전지역 제거
      this.selectedRegionCodes =
        this.selectedRegionCodes.filter(code =>
          String(code) !== '99'
        )

      if (!this.isStoreRequired) {
        this.errors.remove('stores')
      }
    },
  },
  
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      isUpdateMode: false,
      loading2: false,

      userId: '',
      position: null,

      // Region
      regions: [],
      selectedRegionCodes: [],
      regionLoading: false,

      // Store
      stores: [],
      selectedStoreCodes: [],

      storeLoading: false,
    }
  },
  methods: {

    getAppComponent() {
      return this.$parent.$parent.$parent
    },

    async loadRegions() {
      this.regionLoading = true

      try {
        const { data } = await ServerApiCallService.prefecture()
        const regionList = data.prefectures || []

        const mappedRegions = regionList.map(region => ({
          regionCode: region.prefectureCode == null
            ? ''
            : String(region.prefectureCode),
          regionName: region.prefectureName,
          displayName: region.prefectureName
        }))

        // 전지역 코드가 API 결과에 없을 때만 추가
        const hasAllRegion = mappedRegions.some(
          region => region.regionCode === '99'
        )

        this.regions = hasAllRegion
          ? mappedRegions
          : [
              {
                regionCode: '99',
                regionName: '全地域',
                displayName: '全地域'
              },
              ...mappedRegions
            ]
      } catch (error) {
        console.error(
          '地域一覧の取得に失敗しました。',
          error.response || error
        )

        this.regions = []

        this.getAppComponent().showErrorDialog(
          this.$t('loadFailed')
        )
      } finally {
        this.regionLoading = false
      }
    },

    async loadStores() {
      this.storeLoading = true

      try {
        const { data } = await ServerApiCallService.nonDeletedStores()

        const storeList = data.stores || []

        this.stores = storeList.map(store => ({
          storeCode: store.storeCode,
          storeName: store.storeName,
          prefectureCode: store.prefecture == null
            ? ''
            : String(store.prefecture),
          displayName: `${store.storeCode} - ${store.storeName}`,
        }))
      } catch (error) {
        console.error(
          '店舗一覧の取得に失敗しました。',
          error.response || error
        )

        this.stores = []

        this.getAppComponent().showErrorDialog(
          this.$t('loadFailed')
        )
      } finally {
        this.storeLoading = false
      }
    },

    async loadUserMapping(userId) {
      try {
        const { data } =
          await ServerApiCallService
            .ocrUserStoreMappingDetail(userId)

        this.position = data.positionCode || null

        if (this.position === 'ADMIN') {
          // 관리자는 항상 전지역으로 표시
          this.selectedRegionCodes = ['99']
          this.selectedStoreCodes = []
        } else {
          this.selectedRegionCodes =
            (data.regionCodes || []).map(String)

          this.selectedStoreCodes =
            (data.storeCodes || []).map(String)
        }
      } catch (error) {
        console.error(
          'ユーザーマッピング情報の取得に失敗しました。',
          error.response || error
        )

        this.getAppComponent().showErrorDialog(
          this.$t('loadFailed')
        )
      }
    },

    async submit() {
      
      if (this.loading2) {
        return
      }

      const valid = await this.$validator.validateAll()

      // 1. User ID
      if (this.errors.has('userId')) {
        this.getAppComponent().showErrorDialog(
          'User IDを入力してください。'
        )
        return
      }

      // 2. Position
      if (this.errors.has('position')) {
        this.getAppComponent().showErrorDialog(
          'Positionを選択してください。'
        )
        return
      }

      // 3. Region
      if ( this.isRegionRequired && this.errors.has('regions')) {
        this.getAppComponent().showErrorDialog(
          '地域を1つ以上選択してください。'
        )
        return
      }

      // 4. 선택한 지역에 등록된 점포가 있는지 확인
      // 전지역을 제외한 선택 지역만 검사
      const normalSelectedRegionCodes =
        this.selectedRegionCodes.filter(code =>
          String(code) !== '99'
        )

      const registeredStoreRegionCodes = new Set(
        this.stores.map(store =>
          String(store.prefectureCode)
        )
      )

      const regionsWithNoStores =
        normalSelectedRegionCodes.filter(regionCode =>
          !registeredStoreRegionCodes.has(String(regionCode))
        )

      if (
        !this.isAdminPosition &&
        regionsWithNoStores.length > 0
      ) {
        this.getAppComponent().showErrorDialog(
          '店舗がない地域は選択できません。地域を削除してください。'
        )
        return
      }

      // 5. STORE_LEADER와 SALES의 점포 필수 검사
      if (
        this.isStoreRequired &&
        this.errors.has('stores')
      ) {
        this.getAppComponent().showErrorDialog(
          '店舗を1つ以上選択してください。'
        )
        return
      }

      // 6. 나머지 VeeValidate 오류
      if (!valid) {
        this.getAppComponent().showErrorDialog(
          this.$t('hasInputError')
        )
        return
      }

      // 7. 선택한 점포가 선택 지역에 포함되는지 확인
      const validStoreCodes = new Set(
        this.filteredStores.map(store =>
          String(store.storeCode)
        )
      )

      const hasInvalidStore =
        this.selectedStoreCodes.some(storeCode =>
          !validStoreCodes.has(String(storeCode))
        )

      if (hasInvalidStore) {
        this.getAppComponent().showErrorDialog(
          '選択した地域に含まれていない店舗があります。'
        )
        return
      }

      // 8. STORE_LEADER와 SALES는 각 지역에서 점포를 선택해야 함
      if (this.isStoreRequired) {
        const selectedStoreCodeSet = new Set(
          this.selectedStoreCodes.map(String)
        )

        const selectedStores = this.stores.filter(store =>
          selectedStoreCodeSet.has(String(store.storeCode))
        )

        const selectedStoreRegionCodes = new Set(
          selectedStores.map(store =>
            String(store.prefectureCode)
          )
        )

        const regionsWithoutSelectedStore =
          this.selectedRegionCodes.filter(regionCode =>
            !selectedStoreRegionCodes.has(String(regionCode))
          )

        if (regionsWithoutSelectedStore.length > 0) {
          this.getAppComponent().showErrorDialog(
            '各地域から店舗を選択してください。'
          )
          return
        }
      }

      const request = {
        userId: this.userId,
        positionCode: this.position,
        prefectureCodes: this.isAdminPosition
          ? ['99']
          : [...this.selectedRegionCodes],

        storeCodes: this.isAdminPosition
          ? []
          : [...this.selectedStoreCodes]
            }

      const sendContext = {
        hasDialogComponent: this.getAppComponent(),
        forwardNameOnClose: 'admin/ocr-user-store-mapping'
      }

      this.loading2 = true

      try {
        if (this.isUpdateMode) {
          await ServerApiCallService.ocrUserStoreUpdate(
            request,
            sendContext
          )
        } else {
          await ServerApiCallService.ocrUserStoreRegister(
            request,
            sendContext
          )
        }
      } catch (error) {
        console.error(
          '店舗・ユーザーマッピング情報の保存に失敗しました。',
          error.response || error
        )

        this.getAppComponent().showErrorDialog(
          this.$t('saveFailed')
        )
      } finally {
        this.loading2 = false
      }
    }
  }
}
</script>

<style scoped>
.button-area {
  margin-top: 55px;
  margin-bottom: 20px;
}
.required-star {
  margin-left: 3px;
  color: #f44336;
  font-weight: bold;
}
</style>